/**
 * Vervit — gift wrap add-to-cart (main + gift wrap in one Shopify /cart/add.js call).
 */
(function () {
  'use strict';

  function getConfig() {
    return window.vrvGiftWrapConfig && typeof window.vrvGiftWrapConfig === 'object'
      ? window.vrvGiftWrapConfig
      : {};
  }

  function queryRoot(root) {
    if (!root) return document;
    if (root.querySelector) return root;
    if (root instanceof Element) return root;
    return document;
  }

  function getGiftWrapVariantId(root) {
    const cfg = getConfig();
    const scope = queryRoot(root);
    const input = scope.querySelector('[data-vrv-gift-wrap-variant]');
    if (input && input.value) return String(input.value).trim();
    if (cfg.variantId) return String(cfg.variantId).trim();
    return '';
  }

  function isGiftWrapChecked(root) {
    const scope = queryRoot(root);
    const checkbox = scope.querySelector('[data-vrv-gift-wrap]');
    return Boolean(checkbox && checkbox.checked && !checkbox.disabled);
  }

  function shouldIncludeGiftWrap(root, override) {
    if (override === false) return false;
    const variantId = getGiftWrapVariantId(root);
    if (!variantId) return false;
    if (override === true) return true;
    return isGiftWrapChecked(root);
  }

  function parseMainItemFromFormData(formData) {
    if (!formData || typeof formData.get !== 'function') return null;
    const id = Number(formData.get('id'));
    if (!id) return null;

    const item = {
      id,
      quantity: Math.max(1, parseInt(formData.get('quantity'), 10) || 1),
    };

    const properties = {};
    formData.forEach((value, key) => {
      const match = /^properties\[(.+)\]$/.exec(String(key));
      if (match && match[1] && value != null && String(value).trim() !== '') {
        properties[match[1]] = value;
      }
    });
    if (Object.keys(properties).length) item.properties = properties;

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) item.selling_plan = Number(sellingPlan);

    return item;
  }

  function normalizeLineItem(item) {
    if (!item || !item.id) return null;
    const line = {
      id: Number(item.id),
      quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
    };
    if (item.properties && typeof item.properties === 'object' && Object.keys(item.properties).length) {
      line.properties = item.properties;
    }
    if (item.selling_plan) line.selling_plan = Number(item.selling_plan);
    return line;
  }

  function buildGiftWrapLine(variantId) {
    const id = Number(variantId || getGiftWrapVariantId());
    if (!id) return null;
    return {
      id,
      quantity: 1,
      properties: { VrvGiftWrap: 'yes' },
    };
  }

  function getCartElement(explicit) {
    if (explicit !== undefined && explicit !== null) return explicit;
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function getSectionPayload(cartEl) {
    if (!cartEl) return {};
    const helpers = window.vrvCartAddHelpers || {};
    let sectionIds = [];

    if (typeof window.vrvGetCartAddSections === 'function') {
      sectionIds = window.vrvGetCartAddSections();
    } else if (typeof helpers.getCartAddSectionIds === 'function') {
      sectionIds = helpers.getCartAddSectionIds(cartEl);
    } else if (typeof cartEl.getSectionsToRender === 'function') {
      sectionIds = cartEl.getSectionsToRender().map((s) => s.id || s.section);
    }

    if (!sectionIds.length) return {};

    if (typeof cartEl.setActiveElement === 'function') {
      cartEl.setActiveElement(document.activeElement);
    }

    return {
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };
  }

  function getNativeFetch() {
    return (
      window.__vrvTrueNativeFetch ||
      window.__vrvNativeFetch ||
      (typeof window.fetch === 'function' ? window.fetch.bind(window) : null)
    );
  }

  function getCartAddJsUrl() {
    const raw = (window.routes && window.routes.cart_add_url) || '/cart/add';
    if (/\.js(?:\?|$)/.test(String(raw))) return raw;
    return String(raw).replace(/\/$/, '') + '.js';
  }

  function postCartAddJson(body) {
    const url = getCartAddJsUrl();
    const fn = getNativeFetch();
    if (!fn) return Promise.reject(new Error('fetch unavailable'));

    return fn(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    }).then((response) =>
      response
        .json()
        .catch(() => ({}))
        .then((data) => ({
          ok: response.ok,
          status: response.status,
          data: data && typeof data === 'object' ? data : {},
        }))
    );
  }

  function postCartAddFormData(formData) {
    const helpers = window.vrvCartAddHelpers || {};
    if (typeof helpers.fetchCartAdd === 'function') {
      return helpers.fetchCartAdd(formData).then(({ ok, data }) => ({
        ok,
        status: ok ? 200 : 422,
        data: data || {},
      }));
    }
    return postCartAddJson(Object.fromEntries(formData.entries()));
  }

  function isSuccess(result) {
    const helpers = window.vrvCartAddHelpers || {};
    const ok = result && result.ok;
    const data = (result && result.data) || {};
    if (typeof helpers.isCartAddSuccess === 'function') {
      return helpers.isCartAddSuccess(data, ok);
    }
    if (typeof helpers.isCartAddError === 'function') {
      return !helpers.isCartAddError(data, ok);
    }
    return Boolean(ok && (data.items || data.id || data.key || data.sections));
  }

  function logFailure(label, result, extra) {
    console.warn('[VRV gift wrap cart] ' + label, Object.assign(
      {
        httpStatus: result && result.status,
        shopifyMessage: result && result.data && (result.data.description || result.data.message),
        shopifyErrors: result && result.data && (result.data.errors || result.data.items),
      },
      extra || {}
    ));
  }

  function finalizeResult(result, includeGift, giftAdded) {
    const data = (result && result.data) || {};
    return {
      ok: isSuccess(result),
      response: data,
      includeGiftWrap: includeGift,
      giftWrapAdded: Boolean(giftAdded),
      giftWrapFailed: Boolean(includeGift && !giftAdded && isSuccess(result)),
    };
  }

  function addGiftWrapLineOnly(giftLine, cartEl, skipSections) {
    const body = Object.assign({ items: [giftLine] }, skipSections ? {} : getSectionPayload(cartEl));
    return postCartAddJson(body);
  }

  /**
   * Add main product + optional gift wrap.
   * Prefers ONE /cart/add.js JSON request with items: [main, giftWrap].
   */
  function addMainAndGiftWrap(options) {
    const opts = options || {};
    const root = opts.root || document;
    const includeGift = shouldIncludeGiftWrap(root, opts.includeGiftWrap);
    const giftVariantId = opts.giftVariantId
      ? String(opts.giftVariantId).trim()
      : getGiftWrapVariantId(root);
    const skipSections = Boolean(opts.skipSections);
    const cartEl = skipSections ? null : getCartElement(opts.cartEl);

    let mainItem = normalizeLineItem(opts.mainItem);
    if (!mainItem && opts.formData) {
      mainItem = normalizeLineItem(parseMainItemFromFormData(opts.formData));
    }
    if (!mainItem) {
      return Promise.reject(new Error('Please select a product option before adding to cart.'));
    }

    const giftLine = includeGift ? buildGiftWrapLine(giftVariantId) : null;
    if (includeGift && !giftLine) {
      return Promise.reject(
        new Error('Gift wrap is not configured. Please contact the store or try without gift wrap.')
      );
    }

    const items = giftLine ? [mainItem, giftLine] : [mainItem];
    const body = Object.assign({ items }, skipSections ? {} : getSectionPayload(cartEl));

    return postCartAddJson(body).then((result) => {
      if (isSuccess(result)) {
        if (giftLine && typeof window.vrvMaintainGiftWrapDiscountIntegrity === 'function') {
          setTimeout(() => window.vrvMaintainGiftWrapDiscountIntegrity().catch(() => null), 0);
        }
        return finalizeResult(result, includeGift, Boolean(giftLine));
      }

      logFailure('combined add failed', result, { items });

      if (!giftLine) {
        if (opts.formData) {
          return postCartAddFormData(opts.formData).then((formResult) => finalizeResult(formResult, false, false));
        }
        return finalizeResult(result, false, false);
      }

      // Fallback: main first, then gift wrap (legacy path).
      const mainOnlyBody = Object.assign({ items: [mainItem] }, skipSections ? {} : getSectionPayload(cartEl));
      return postCartAddJson(mainOnlyBody).then((mainResult) => {
        if (!isSuccess(mainResult)) {
          return finalizeResult(mainResult, includeGift, false);
        }
        return addGiftWrapLineOnly(giftLine, cartEl, skipSections).then((giftResult) => {
          if (!isSuccess(giftResult)) {
            logFailure('gift wrap fallback add failed', giftResult, { giftVariantId });
            return Object.assign(finalizeResult(mainResult, true, false), {
              giftWrapFailed: true,
              giftWrapError: giftResult.data,
            });
          }
          return finalizeResult(giftResult, true, true);
        });
      });
    });
  }

  /** Buy Now — empty cart then add main + gift wrap atomically. */
  function addBuyNowLines(mainItem, giftVariantId) {
    const main = normalizeLineItem(mainItem);
    if (!main) {
      return Promise.reject(new Error('Could not add product for Buy Now checkout. Please try again.'));
    }

    const giftId = giftVariantId ? String(giftVariantId).trim() : '';
    const items = [main];
    const giftLine = giftId ? buildGiftWrapLine(giftId) : null;
    if (giftLine) items.push(giftLine);

    return postCartAddJson({ items }).then((result) => {
      if (isSuccess(result)) {
        return { ok: true, data: result.data, cart: result.data };
      }
      logFailure('buy now add failed', result, { items });
      throw new Error(
        (result.data && (result.data.description || result.data.message)) ||
          'Could not add product for Buy Now checkout. Please try again.'
      );
    });
  }

  window.vrvGiftWrapCart = {
    getConfig,
    getGiftWrapVariantId,
    isGiftWrapChecked,
    shouldIncludeGiftWrap,
    parseMainItemFromFormData,
    buildGiftWrapLine,
    addMainAndGiftWrap,
    addBuyNowLines,
  };

  window.vrvAddProductWithGiftWrap = function (options) {
    return window.vrvGiftWrapCart.addMainAndGiftWrap(options);
  };
})();
